import React from 'react';
import { ArrowLeft, Brain } from 'lucide-react';

const TermsAndConditions = ({ onBack }) => {
  return (
    <div className="min-h-screen" style={{ background: '#0D1117' }}>
      {/* Header */}
      <div className="py-8" style={{ background: '#111827', borderBottom: '1px solid #1F2937' }}>
        <div className="max-w-4xl mx-auto px-4">
          <button
            onClick={onBack}
            className="flex items-center space-x-2 text-gray-400 hover:text-white transition-colors mb-4"
          >
            <ArrowLeft size={18} />
            <span>Back to Dashboard</span>
          </button>
          <div className="flex items-center space-x-3">
            <Brain size={28} className="text-blue-400" />
            <div>
              <h1 className="text-3xl font-bold text-white">Terms &amp; Conditions</h1>
              <p className="text-gray-400 text-sm mt-1">Last Updated: February 25, 2026</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-10 space-y-8 text-gray-700 leading-relaxed">

        {/* 1. Introduction */}
        <section>
          <h2 className="text-2xl font-bold text-gray-900 mb-3 pb-2 border-b border-gray-200">
            1. Introduction and Acceptance
          </h2>
          <p>
            Welcome to <strong>TradeFix AI</strong> ("we", "our", or "us"), an AI-powered trading psychology
            platform operated by Krish Automation Services. By accessing or using TradeFix AI at any URL or
            application where these Terms are posted, you ("User" or "you") agree to be legally bound by
            these Terms &amp; Conditions ("Terms").
          </p>
          <p className="mt-3">
            If you do not agree to these Terms, please do not access or use our services. Your continued
            use of TradeFix AI after any changes to these Terms constitutes your acceptance of those changes.
          </p>
        </section>

        {/* 2. User Accounts */}
        <section>
          <h2 className="text-2xl font-bold text-gray-900 mb-3 pb-2 border-b border-gray-200">
            2. User Accounts &amp; Registration
          </h2>
          <p>To use TradeFix AI, you must create an account. By registering, you agree to:</p>
          <ol className="list-decimal list-inside mt-3 space-y-2 pl-2">
            <li>Provide accurate, current, and complete information during registration.</li>
            <li>Maintain the security of your password and account credentials.</li>
            <li>Promptly notify us of any unauthorized use of your account at <a href="mailto:krishautomation.services@gmail.com" className="text-blue-600 hover:underline">krishautomation.services@gmail.com</a>.</li>
            <li>Accept responsibility for all activities that occur under your account.</li>
            <li>Not share your account with any other person or allow others to use your account.</li>
          </ol>
          <p className="mt-3">
            We reserve the right to suspend or terminate accounts that we believe are being used
            fraudulently, in violation of these Terms, or in a manner harmful to other users.
          </p>
        </section>

        {/* 3. Subscription Plans */}
        <section>
          <h2 className="text-2xl font-bold text-gray-900 mb-3 pb-2 border-b border-gray-200">
            3. Subscription Plans
          </h2>
          <p>TradeFix AI offers the following subscription tiers:</p>

          <div className="mt-4 space-y-4">
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <h3 className="font-bold text-gray-900 text-lg">Free Plan</h3>
              <p className="text-sm mt-1">Access to core features with a limit of <strong>10 trades</strong> maximum. No payment required. Ideal for evaluation purposes.</p>
            </div>
            <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
              <h3 className="font-bold text-gray-900 text-lg">Pro Plan — ₹999/month</h3>
              <p className="text-sm mt-1">Unlimited trade logging, AI Insights, Psychology Tracker, Risk Manager, and all core features. Billed monthly via our payment gateway partner.</p>
            </div>
            <div className="bg-indigo-50 rounded-lg p-4 border border-indigo-200">
              <h3 className="font-bold text-gray-900 text-lg">Elite Plan — ₹1,999/month</h3>
              <p className="text-sm mt-1">Everything in Pro, plus advanced AI coaching, priority support, and all future Elite-tier features. Billed monthly via our payment gateway partner.</p>
            </div>
          </div>

          <p className="mt-4">
            Plan features and pricing are subject to change with reasonable advance notice to subscribers.
            Continued use of the service after a pricing change takes effect constitutes acceptance of the
            new pricing.
          </p>
        </section>

        {/* 4. Payment Terms */}
        <section>
          <h2 className="text-2xl font-bold text-gray-900 mb-3 pb-2 border-b border-gray-200">
            4. Payment Terms
          </h2>
          <ol className="list-decimal list-inside space-y-2 pl-2">
            <li>All payments are processed securely via <strong>our payment gateway partner</strong>, a PCI-DSS compliant payment gateway. We do not store your card details.</li>
            <li>Subscriptions are billed on a monthly basis from the date of initial purchase.</li>
            <li>All prices are in Indian Rupees (INR) and inclusive of applicable taxes.</li>
            <li>Failed payments may result in temporary suspension of paid features until payment is resolved.</li>
            <li>You authorize us to charge your selected payment method for recurring subscription fees.</li>
          </ol>
        </section>

        {/* 5. Money-back Guarantee */}
        <section>
          <h2 className="text-2xl font-bold text-gray-900 mb-3 pb-2 border-b border-gray-200">
            5. 14-Day Money-Back Guarantee
          </h2>
          <p>
            We stand behind the quality of TradeFix AI. If you are not satisfied with your subscription,
            you may request a full refund within <strong>14 days</strong> of the initial purchase date.
          </p>
          <p className="mt-3">This guarantee applies to:</p>
          <ul className="list-disc list-inside mt-2 space-y-1 pl-2">
            <li>Pro Plan (first-time subscribers only)</li>
            <li>Elite Plan (first-time subscribers only)</li>
          </ul>
          <p className="mt-3">
            To request a refund, contact us at{' '}
            <a href="mailto:krishautomation.services@gmail.com" className="text-blue-600 hover:underline">
              krishautomation.services@gmail.com
            </a>{' '}
            or WhatsApp{' '}
            <a href="https://wa.me/919373606511" className="text-blue-600 hover:underline">
              +91-9373606511
            </a>. Refunds are processed within 5–7 business days. See our full Refund Policy for details.
          </p>
        </section>

        {/* 6. Service Usage Rules */}
        <section>
          <h2 className="text-2xl font-bold text-gray-900 mb-3 pb-2 border-b border-gray-200">
            6. Service Usage Rules and Restrictions
          </h2>
          <p>You agree to use TradeFix AI only for lawful purposes. You must not:</p>
          <ol className="list-decimal list-inside mt-3 space-y-2 pl-2">
            <li>Use the platform to engage in any fraudulent, deceptive, or illegal activity.</li>
            <li>Attempt to gain unauthorized access to any part of the service or its infrastructure.</li>
            <li>Reverse engineer, decompile, or disassemble any portion of the platform.</li>
            <li>Scrape, crawl, or extract data from TradeFix AI using automated means without prior written consent.</li>
            <li>Transmit any malicious code, viruses, or disruptive content.</li>
            <li>Impersonate any person or entity or misrepresent your affiliation with any person or entity.</li>
            <li>Resell, sublicense, or otherwise commercialize the service without our written consent.</li>
          </ol>
          <p className="mt-3">
            <strong>Important Disclaimer:</strong> TradeFix AI is an analytical and educational tool. It does
            not provide financial, investment, or trading advice. All trading decisions are solely your
            responsibility. Past performance insights do not guarantee future results.
          </p>
        </section>

        {/* 7. Intellectual Property */}
        <section>
          <h2 className="text-2xl font-bold text-gray-900 mb-3 pb-2 border-b border-gray-200">
            7. Intellectual Property Rights
          </h2>
          <p>
            All content, features, and functionality of TradeFix AI — including but not limited to software,
            text, graphics, logos, icons, and AI models — are the exclusive property of Krish Automation
            Services and are protected by applicable copyright, trademark, and intellectual property laws.
          </p>
          <p className="mt-3">
            You retain ownership of your personal trading data entered into the platform. By using TradeFix AI,
            you grant us a limited, non-exclusive license to process and store your data solely for the
            purpose of providing the service to you.
          </p>
        </section>

        {/* 8. Limitation of Liability */}
        <section>
          <h2 className="text-2xl font-bold text-gray-900 mb-3 pb-2 border-b border-gray-200">
            8. Limitation of Liability
          </h2>
          <p>
            To the maximum extent permitted by applicable law, Krish Automation Services shall not be
            liable for any indirect, incidental, special, consequential, or punitive damages — including
            loss of profits, data, or trading capital — arising out of or in connection with your use of
            TradeFix AI, even if we have been advised of the possibility of such damages.
          </p>
          <p className="mt-3">
            Our total cumulative liability to you for any claims arising from your use of TradeFix AI shall
            not exceed the amount you paid to us in the three (3) months preceding the claim.
          </p>
        </section>

        {/* 9. Account Termination */}
        <section>
          <h2 className="text-2xl font-bold text-gray-900 mb-3 pb-2 border-b border-gray-200">
            9. Account Termination
          </h2>
          <p>
            You may delete your account at any time by contacting us. We may suspend or terminate your
            account immediately, without prior notice, if you breach these Terms or engage in conduct that
            we determine, at our sole discretion, is harmful to other users or the platform.
          </p>
          <p className="mt-3">
            Upon termination, your right to access TradeFix AI ceases immediately. We may retain your data
            as required by law or for legitimate business purposes, subject to our Privacy Policy.
          </p>
        </section>

        {/* 10. Modifications */}
        <section>
          <h2 className="text-2xl font-bold text-gray-900 mb-3 pb-2 border-b border-gray-200">
            10. Modifications to Terms
          </h2>
          <p>
            We reserve the right to modify these Terms at any time. When we make significant changes, we
            will notify registered users via email or an in-app notification at least 7 days before the
            changes take effect. Your continued use of TradeFix AI after the effective date constitutes
            acceptance of the revised Terms.
          </p>
        </section>

        {/* 11. Governing Law */}
        <section>
          <h2 className="text-2xl font-bold text-gray-900 mb-3 pb-2 border-b border-gray-200">
            11. Governing Law &amp; Dispute Resolution
          </h2>
          <p>
            These Terms shall be governed by and construed in accordance with the laws of India. Any
            disputes arising out of or relating to these Terms or your use of TradeFix AI shall be subject
            to the exclusive jurisdiction of the courts located in India. We encourage you to contact us
            first to resolve any disputes amicably before pursuing legal action.
          </p>
        </section>

        {/* 12. Contact */}
        <section>
          <h2 className="text-2xl font-bold text-gray-900 mb-3 pb-2 border-b border-gray-200">
            12. Contact Us
          </h2>
          <p>If you have any questions about these Terms &amp; Conditions, please contact us:</p>
          <div className="mt-4 bg-gray-50 rounded-lg p-5 border border-gray-200 space-y-2">
            <p><strong>Krish Automation Services</strong></p>
            <p>
              📧 Email:{' '}
              <a href="mailto:krishautomation.services@gmail.com" className="text-blue-600 hover:underline">
                krishautomation.services@gmail.com
              </a>
            </p>
            <p>
              💬 WhatsApp:{' '}
              <a href="https://wa.me/919373606511" className="text-blue-600 hover:underline">
                +91-9373606511
              </a>{' '}
              (Message only)
            </p>
            <p className="text-sm text-gray-500">Response within 24 hours | Mon–Sat, 10 AM – 6 PM IST</p>
          </div>
        </section>

        {/* Back button */}
        <div className="pt-4 border-t border-gray-200">
          <button
            onClick={onBack}
            className="flex items-center space-x-2 bg-blue-600 text-white px-5 py-2.5 rounded-lg hover:bg-blue-700 transition-colors font-medium"
          >
            <ArrowLeft size={16} />
            <span>Back to Dashboard</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default TermsAndConditions;
